/**
 * Spec 104 — Rule-Set Health panel (read-only).
 *
 * Three sections, each with populated rows or an explicit empty state:
 *   1. Redundant Rule Pairs   — active-rule embedding cosine > 0.90
 *   2. Coverage Gaps          — spec-103 proposal centroids with no nearby rule
 *   3. Low-Utility Rules       — all-pass/all-fail over >= N evals; thin samples
 *                                shown as "insufficient data" (not flagged)
 *
 * Surface-only (v1 — clarifications Q1): no merge/prune action. Each row links
 * to the relevant rule(s) or proposal (FR-7). Fetches `/health` on mount.
 *
 * Returns a role-gated render for non-admins (defense-in-depth; the page is
 * already behind ProtectedRoute). data-testids wire gate 104.A.6:
 *   rule-health-panel, health-redundant-pair, health-coverage-gap,
 *   health-low-utility, health-redundant-empty, health-coverage-empty,
 *   health-utility-empty.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from './ui/Icon.js';
import { getRuleSetHealth, type RuleSetHealth } from '../api/ruleIngestion.js';
import { useAuth } from '../lib/auth-context.js';

export function RuleHealthPanel() {
  const { hasRole } = useAuth();
  const canView = hasRole('rule_admin') || hasRole('orbit:admin');

  const [data, setData] = useState<RuleSetHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getRuleSetHealth()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // FR-8 — non-admins get nothing (DOM-absent panel body).
  if (!canView) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
        Rule-set health requires the rule_admin role.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-4 py-6 text-center text-sm text-slate-500">
        Computing rule-set health…
      </div>
    );
  }

  if (err) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        {err}
      </div>
    );
  }

  if (!data) return null;

  const { redundant_pairs, coverage_gaps, low_utility_rules, thresholds } = data;

  return (
    <div data-testid="rule-health-panel" className="space-y-6">
      <p className="text-sm text-slate-600">
        Read-only curation signals computed from stored embeddings, proposal
        centroids, and evaluation outcomes. Maximize coverage, minimize
        redundancy, drop dead weight. Act via the Rules tab (deprecate / sharpen)
        — this panel surfaces, it does not mutate.
      </p>

      {/* ─── Redundant Rule Pairs ─────────────────────────────────── */}
      <section className="rounded-lg border border-slate-200 bg-white">
        <header className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <Icon name="bx-copy" className="text-violet-600" />
          <h2 className="text-base font-semibold text-slate-900">
            Redundant rule pairs
          </h2>
          <span className="ml-auto text-xs text-slate-500">
            cosine &gt; {thresholds.redundancy.toFixed(2)}
          </span>
        </header>
        <div className="p-4">
          {redundant_pairs.length === 0 ? (
            <p
              data-testid="health-redundant-empty"
              className="text-sm text-slate-500"
            >
              No redundant rules — the library is well-separated.
            </p>
          ) : (
            <ul className="space-y-2">
              {redundant_pairs.map((p) => (
                <li
                  key={`${p.rule_a.id}-${p.rule_b.id}`}
                  data-testid="health-redundant-pair"
                  className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                >
                  <Link
                    to={`/admin/rules/${p.rule_a.id}`}
                    className="font-mono text-xs text-violet-600 hover:underline"
                    title={p.rule_a.name}
                  >
                    {p.rule_a.slug}
                  </Link>
                  <span className="text-slate-400">↔</span>
                  <Link
                    to={`/admin/rules/${p.rule_b.id}`}
                    className="font-mono text-xs text-violet-600 hover:underline"
                    title={p.rule_b.name}
                  >
                    {p.rule_b.slug}
                  </Link>
                  <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    {p.similarity.toFixed(3)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ─── Coverage Gaps ────────────────────────────────────────── */}
      <section className="rounded-lg border border-slate-200 bg-white">
        <header className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <Icon name="bx-target-lock" className="text-violet-600" />
          <h2 className="text-base font-semibold text-slate-900">
            Coverage gaps
          </h2>
          <span className="ml-auto text-xs text-slate-500">
            nearest rule &lt; {thresholds.coverage.toFixed(2)}
          </span>
        </header>
        <div className="p-4">
          {coverage_gaps.length === 0 ? (
            <p
              data-testid="health-coverage-empty"
              className="text-sm text-slate-500"
            >
              No detected gaps.
            </p>
          ) : (
            <ul className="space-y-2">
              {coverage_gaps.map((g) => (
                <li
                  key={g.proposal_id}
                  data-testid="health-coverage-gap"
                  className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                >
                  <span className="text-slate-700">
                    You're missing a rule for:{' '}
                    <Link
                      to={`/admin/rules/proposals?highlight=${g.proposal_id}`}
                      className="font-medium text-violet-600 hover:underline"
                    >
                      {g.label}
                    </Link>
                  </span>
                  <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                    nearest {g.nearest_rule_similarity.toFixed(3)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ─── Low-Utility Rules ────────────────────────────────────── */}
      <section className="rounded-lg border border-slate-200 bg-white">
        <header className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <Icon name="bx-pulse" className="text-violet-600" />
          <h2 className="text-base font-semibold text-slate-900">
            Low-utility rules
          </h2>
          <span className="ml-auto text-xs text-slate-500">
            0% / 100% over ≥ {thresholds.min_n} evals
          </span>
        </header>
        <div className="p-4">
          {low_utility_rules.length === 0 ? (
            <p
              data-testid="health-utility-empty"
              className="text-sm text-slate-500"
            >
              No low-utility rules detected.
            </p>
          ) : (
            <ul className="space-y-2">
              {low_utility_rules.map((u) => (
                <li
                  key={u.rule_id}
                  data-testid="health-low-utility"
                  className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                >
                  {!u.insufficient_data && (
                    <Icon name="bx-error" className="shrink-0 text-amber-600" />
                  )}
                  <Link
                    to={`/admin/rules/${u.rule_id}`}
                    className="font-mono text-xs text-violet-600 hover:underline"
                    title={u.name}
                  >
                    {u.slug}
                  </Link>
                  {u.insufficient_data ? (
                    <span className="ml-auto rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                      insufficient data ({u.evaluation_count})
                    </span>
                  ) : (
                    <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                      {Math.round(u.pass_rate * 100)}% pass · n={u.evaluation_count}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
